import { HttpClient } from '../shared/api/HttpClient';
import type { TrackSummary } from '../segue/types';
import type { StationCommandReceipt, StationSummary, StudioCommandType, StudioSnapshot } from './types';

interface Page<T> { items: T[]; nextCursor: string | null }

export class StudioApi {
  readonly #http: HttpClient;

  public constructor(http = new HttpClient({ readCsrfToken: readCsrfCookie })) {
    this.#http = http;
  }

  public async stations(): Promise<StationSummary[]> {
    return (await this.#http.request<Page<StationSummary>>('/stations?limit=100')).items;
  }

  public async login(email: string, password: string): Promise<void> {
    await this.#http.request('/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  }

  public snapshot(stationId: string): Promise<StudioSnapshot> {
    return this.#http.request(`/stations/${stationId}/studio`);
  }

  public async tracks(): Promise<TrackSummary[]> {
    const tracks: TrackSummary[] = [];
    let cursor: string | null = null;
    do {
      const page: Page<TrackSummary> = await this.#http.request(`/tracks?limit=100${cursor === null ? '' : `&cursor=${encodeURIComponent(cursor)}`}`);
      tracks.push(...page.items);
      cursor = page.nextCursor;
    } while (cursor !== null);

    return tracks;
  }

  public createQueueInsert(stationId: string, trackId: string, scheduledAt: string): Promise<unknown> {
    return this.#http.request(`/stations/${stationId}/manual-inserts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: crypto.randomUUID(), trackId, scheduledAt }),
    });
  }

  public command(
    stationId: string,
    type: StudioCommandType,
    expectedRuntimeRevision: number,
    parameters: Record<string, string | number | boolean | null> = {},
    idempotencyKey: string = crypto.randomUUID(),
    liveSessionId: string | null = null,
  ): Promise<StationCommandReceipt> {
    return this.#http.request(`/stations/${stationId}/commands`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({ type, expectedRuntimeRevision, liveSessionId, parameters }),
    });
  }

  public audioUrl(trackId: string): string {
    return `/api/v1/tracks/${trackId}/audio`;
  }
}

function readCsrfCookie(): string | null {
  const prefix = '__Host-radio_csrf=';
  const value = document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith(prefix));
  return value === undefined ? null : decodeURIComponent(value.slice(prefix.length));
}
